<script setup lang="ts">
import { formatPrice } from '#shared/utils/currency'
const route = useRoute()

const { transparentHeader } = defineProps<{
  transparentHeader?: Boolean
}>()

const wishlist = useWishlistStore()

const open = ref(false)

watch(
  () => wishlist.count,
  (newValue, oldValue) => {
    if (newValue === 0) {
      open.value = false
    }

    if (newValue > oldValue) {
      open.value = true
    }
  },
  { deep: true }
)

watch(
  () => route,
  () => {
    open.value = false
  },
  { deep: true }
)
</script>

<template>
  <UDrawer
    direction="right"
    v-model:open="open"
    :ui="{ content: 'w-full max-w-[420px]' }"
  >
    <UButton
      color="primary"
      variant="ghost"
      icon="i-lucide-heart"
      size="lg"
      :class="['relative px-2', { dark: transparentHeader }]"
    >
      <span
        v-if="wishlist.count"
        class="absolute top-0.5 right-0.5 min-w-4 h-4 rounded-full flex items-center justify-center text-[10px] shadow-xs"
        :class="[
          { 'bg-stone-50 text-stone-950': transparentHeader },
          { 'bg-stone-950 text-white': !transparentHeader }
        ]"
      >
        {{ wishlist.count }}
      </span>
    </UButton>

    <template #header>
      <div class="flex justify-between items-center mb-4 md:mt-2.5">
        <h4 class="font-medium text-lg uppercase">Wishlist</h4>

        <UButton
          color="neutral"
          variant="ghost"
          size="md"
          icon="i-lucide-x"
          @click="open = false"
        />
      </div>

      <USeparator color="neutral" />
      <p
        class="mt-2 text-xs text-shadow-stone-500 uppercase font-light text-center"
      >
        Free complimentary shipping on all EU orders
      </p>
    </template>

    <template #body>
      <UAlert
        class="mt-4 w-[330px]"
        variant="outline"
        icon="i-heroicons-information-circle"
        v-if="!wishlist.count"
        title="Your shopping cart is empty"
        description="Looks like you haven’t added anything yet."
      >
      </UAlert>
      <div v-else class="h-full w-full flex flex-col justify-between">
        <div>
          <NuxtLinkLocale
            v-for="item in wishlist?.items"
            :to="`/collections/arche/${item.product.slug}-${item.slug}`"
            class="flex items-center justify-between gap-2 md:gap-3 mt-6"
          >
            <NuxtImg
              :src="item.thumbnail.url"
              alt="alternativeText"
              provider="strapi"
              width="100"
            />

            <div class="w-full">
              <h4 class="font-medium mb-0.5">
                {{ item.product.name }} {{ item.name }}
              </h4>
              <p class="text-sm">
                {{ formatPrice(item.product.price) }}
              </p>
            </div>

            <div class="shrink-1 flex flex-col justify-end items-center gap-1">
              <UButton
                color="neutral"
                variant="link"
                size="xs"
                @click.stop="wishlist.toggle(item)"
                class="text-[10px] p-0"
              >
                Remove
              </UButton>
            </div>
          </NuxtLinkLocale>
        </div>

        <footer>
          <USeparator color="neutral" />
        </footer>
      </div>
    </template>
  </UDrawer>
</template>
